import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import profileInfoActions from 'models/ProfileInfo/actions'
import { getBalance, getJackpotAutoPledgeAmount } from 'models/ProfileInfo/selectors'
import { requestJackpotPledge, updateJackpotPledge, deleteJackpotPledge } from 'config/consumerApiAxiosInstance'
import { EVENT_TYPE, STRING } from 'resources'
import { sendAnalyticsEvent } from 'services/analytics'
import { convertAmountToPoints, convertPointsToAmount } from 'utils/format-points'
import getDecimalValue from 'utils/get-decimal-value'

const useJackpotPledges = ({ currentPledge, fetchData }) => {
  const dispatch = useDispatch()
  const balance = useSelector(getBalance)
  const jackpotAutoPledgeAmount = useSelector(getJackpotAutoPledgeAmount)
  const balanceRef = useRef(balance)

  const [hasChangedPledge, setHasChangedPledge] = useState(false)
  const [isUpdatingPledge, setIsUpdatingPledge] = useState(false)
  const [isDeletingPledge, setIsDeletingPledge] = useState(false)
  const [autoPledgeEnabled, setAutoPledgeEnabled] = useState(!!jackpotAutoPledgeAmount && jackpotAutoPledgeAmount > 0)
  const [showRequestComplete, setShowRequestComplete] = useState(false)
  const [pledgeErrorMessage, setPledgeErrorMessage] = useState('')

  const hasPledge = parseFloat(currentPledge) > 0
  const [selectedAmount, setSelectedAmount] = useState(() => {
    if (hasPledge) {
      return currentPledge
    }
    if (!!jackpotAutoPledgeAmount && jackpotAutoPledgeAmount > 0) {
      return jackpotAutoPledgeAmount
    }
    return balance
  })

  // Define valid pledge boundaries
  const minRedemptionValue = 0.01
  const maxRedemptionValue = getDecimalValue(currentPledge).add(getDecimalValue(balance))
  const minPoints = convertAmountToPoints(minRedemptionValue)
  const maxUserPoints = convertAmountToPoints(maxRedemptionValue)
  const stableMinPoints = useRef(minPoints)
  const stableMaxUserPoints = useRef(maxUserPoints)

  // Determine pledge validity
  const isInvalid = hasPledge && !hasChangedPledge
  const isUpdatedPledge = selectedAmount !== currentPledge

  // Update selected amount to balance when user has no pledge
  balanceRef.current = balance
  useEffect(() => {
    if (!!jackpotAutoPledgeAmount && jackpotAutoPledgeAmount > 0) {
      setSelectedAmount(jackpotAutoPledgeAmount)
    } else if (!hasPledge) {
      setSelectedAmount(balanceRef.current)
    }
  }, [jackpotAutoPledgeAmount, hasPledge])

  // Don't update slider range while updating pledge amount
  useEffect(() => {
    if (!isUpdatingPledge && !isDeletingPledge) {
      stableMinPoints.current = minPoints
      stableMaxUserPoints.current = maxUserPoints
    }
  }, [isUpdatingPledge, isDeletingPledge, minPoints, maxUserPoints])

  const handleRequestAmountChange = useCallback(
    (value) => {
      setHasChangedPledge(true)
      setShowRequestComplete(false)
      setPledgeErrorMessage('')
      if (!value || value >= 0) {
        const valueToNumber = convertPointsToAmount(parseInt(value, 10) || 0)
        setSelectedAmount(valueToNumber.toFixed(2))
      }
    },
    [setSelectedAmount],
  )

  const toggleAutoPledge = useCallback(() => {
    setAutoPledgeEnabled((prev) => {
      if (prev) {
        if (jackpotAutoPledgeAmount) {
          updateJackpotPledge({ amount: currentPledge, is_auto: false })
        }
      }
      return !prev
    })
  }, [jackpotAutoPledgeAmount, currentPledge])

  const onRequestPledge = useCallback(async () => {
    setIsUpdatingPledge(true)
    setPledgeErrorMessage('')
    try {
      if (hasPledge) {
        sendAnalyticsEvent(EVENT_TYPE.jackpotEdit, { auto: autoPledgeEnabled })
        await updateJackpotPledge({ amount: selectedAmount, is_auto: autoPledgeEnabled })
      } else {
        sendAnalyticsEvent(EVENT_TYPE.jackpotAdd, { auto: autoPledgeEnabled })
        await requestJackpotPledge({ amount: selectedAmount, is_auto: autoPledgeEnabled })
      }
      dispatch(profileInfoActions.fetchProfileInfo.init())
      await fetchData()
      setShowRequestComplete(true)
    } catch {
      setPledgeErrorMessage(STRING.jackpot.requestPledgeErrorMessage)
    } finally {
      setIsUpdatingPledge(false)
    }
  }, [dispatch, hasPledge, autoPledgeEnabled, selectedAmount, fetchData])

  const onDeletePledge = useCallback(async () => {
    setIsDeletingPledge(true)
    setPledgeErrorMessage('')
    setHasChangedPledge(false)
    try {
      sendAnalyticsEvent(EVENT_TYPE.jackpotDelete)
      await deleteJackpotPledge()
      dispatch(profileInfoActions.fetchProfileInfo.init())
      await fetchData()
      setShowRequestComplete(true)
    } catch {
      setPledgeErrorMessage(STRING.jackpot.deletePledgeErrorMessage)
    } finally {
      setIsDeletingPledge(false)
    }
  }, [dispatch, fetchData])

  return {
    minPoints: stableMinPoints.current,
    maxUserPoints: stableMaxUserPoints.current,
    selectedAmount,
    isInvalid,
    isUpdatedPledge,
    hasPledge,
    isUpdatingPledge,
    isDeletingPledge,
    autoPledgeEnabled,
    showRequestComplete,
    pledgeErrorMessage,
    handleRequestAmountChange,
    toggleAutoPledge,
    onRequestPledge,
    onDeletePledge,
  }
}

useJackpotPledges.propTypes = {
  currentPledge: PropTypes.string.isRequired,
  fetchData: PropTypes.func.isRequired,
}

export default useJackpotPledges
